import { useState, useCallback, useRef, useEffect } from 'react';
import SpeedTest from '@cloudflare/speedtest';
import * as Sentry from '@sentry/react';

export interface SpeedTestResult {
  downloadSpeed: number; // Mbps
  uploadSpeed: number; // Mbps
  latency: number; // milliseconds
  jitter: number; // ms — variance between consecutive pings, key for call quality
  quality: 'excellent' | 'good' | 'fair' | 'poor';
  recommendation: string;
  meetsMinimumRequirement: boolean; // true if download >= 10 Mbps and upload >= 3 Mbps
}

export const MINIMUM_DOWNLOAD_SPEED_MBPS = 10;
export const MINIMUM_UPLOAD_SPEED_MBPS = 3;

export interface SpeedTestState {
  isRunning: boolean;
  stage: 'idle' | 'downloading' | 'uploading' | 'complete' | 'error';
  progress: number; // 0-100
  result: SpeedTestResult | null;
  error: string | null;
}

// Progress ranges per Cloudflare measurement phase
const PHASE_RANGES = {
  latency:    { start: 5,  end: 20,  stage: 'downloading' as const },
  download:   { start: 20, end: 65,  stage: 'downloading' as const },
  upload:     { start: 65, end: 92,  stage: 'uploading'   as const },
  packetLoss: { start: 92, end: 98,  stage: 'uploading'   as const },
};

type CFPhase = keyof typeof PHASE_RANGES;

function getQuality(dl: number, ul: number): SpeedTestResult['quality'] {
  if (dl >= 20 && ul >= 5)   return 'excellent';
  if (dl >= 10 && ul >= 3)   return 'good';
  if (dl >= 5  && ul >= 1.5) return 'fair';
  return 'poor';
}

function getRecommendation(quality: SpeedTestResult['quality'], ul: number): string {
  switch (quality) {
    case 'excellent':
      return 'Excellent connection! HD video interviews with screen sharing will work perfectly.';
    case 'good':
      return 'Good connection quality. Video interviews should work smoothly.';
    case 'fair':
      return ul < MINIMUM_UPLOAD_SPEED_MBPS
        ? 'Fair upload speed. Video quality may be reduced. Consider moving closer to your router.'
        : 'Fair connection. Video interviews may have occasional quality issues.';
    case 'poor':
      return ul < 1
        ? 'Poor upload speed. You may experience video freezing or audio drops. Move closer to your router or close background apps.'
        : 'Slow connection detected. Video interviews may have significant quality issues. Consider contacting your ISP.';
  }
}

export const useSpeedTest = () => {
  const [state, setState] = useState<SpeedTestState>({
    isRunning: false,
    stage: 'idle',
    progress: 0,
    result: null,
    error: null,
  });
  const engineRef = useRef<SpeedTest | null>(null);

  useEffect(() => {
    return () => {
      engineRef.current?.pause();
      engineRef.current = null;
    };
  }, []);

  const runSpeedTest = useCallback((): void => {
    engineRef.current?.pause();
    engineRef.current = null;

    setState({ isRunning: true, stage: 'downloading', progress: 5, result: null, error: null });

    const engine = new SpeedTest({ autoStart: false });
    engineRef.current = engine;

    let currentPhase: CFPhase = 'latency';

    engine.onPhaseChange = ({ measurement }) => {
      const type = measurement.type as CFPhase;
      if (type in PHASE_RANGES) {
        currentPhase = type;
        setState(prev => ({
          ...prev,
          stage: PHASE_RANGES[type].stage,
          progress: PHASE_RANGES[type].start,
        }));
      }
    };

    engine.onResultsChange = () => {
      const { start, end } = PHASE_RANGES[currentPhase];
      const step = Math.round((end - start) / 8);
      setState(prev => ({
        ...prev,
        progress: Math.min(end - 1, prev.progress + step),
      }));
    };

    engine.onFinish = (results) => {
      const downloadBps = results.getDownloadBandwidth() ?? 0;
      const uploadBps   = results.getUploadBandwidth()   ?? 0;
      const latency     = Math.round(results.getUnloadedLatency() ?? 0);
      const jitter      = Math.round((results.getUnloadedJitter() ?? 0) * 10) / 10;

      const downloadSpeed = Math.round((downloadBps / 1_000_000) * 100) / 100;
      const uploadSpeed   = Math.round((uploadBps   / 1_000_000) * 100) / 100;

      const quality        = getQuality(downloadSpeed, uploadSpeed);
      const recommendation = getRecommendation(quality, uploadSpeed);

      const result: SpeedTestResult = {
        downloadSpeed,
        uploadSpeed,
        latency,
        jitter,
        quality,
        recommendation,
        meetsMinimumRequirement: downloadSpeed >= MINIMUM_DOWNLOAD_SPEED_MBPS &&
          uploadSpeed >= MINIMUM_UPLOAD_SPEED_MBPS,
      };

      setState({
        isRunning: false,
        stage: 'complete',
        progress: 100,
        result,
        error: null,
      });

      // Log to console for Sentry collection
      console.log('Speed Test Results:', {
        downloadSpeed,
        uploadSpeed,
        latency,
        jitter,
        quality,
        timestamp: new Date().toISOString(),
      });

      // Log successful test for analytics
      Sentry.addBreadcrumb({
        category: 'speed-test',
        message: 'Speed test completed',
        data: {
          downloadSpeed,
          uploadSpeed,
          latency,
          jitter,
          quality,
          timestamp: new Date().toISOString(),
        },
        level: 'info',
      });

      // Log warning if upload speed is concerning
      if (uploadSpeed < 1) {
        Sentry.captureMessage('Low upload speed detected - potential interview issues', {
          level: 'warning',
          tags: {
            component: 'speed-test',
            quality: quality,
          },
          extra: {
            downloadSpeed,
            uploadSpeed,
            latency,
            jitter,
            quality,
          }
        });
      } else if (uploadSpeed < 1.5) {
        Sentry.captureMessage('Fair upload speed - may affect video quality', {
          level: 'info',
          tags: { 
            component: 'speed-test', 
            quality: quality,
          },
          extra: { 
            downloadSpeed, 
            uploadSpeed, 
            latency, 
            jitter, 
            quality, 
          }
        });
      }
    };

    engine.onError = (error) => {
      if (engine.isRunning) {
        console.warn('Speed test non-fatal error (continuing):', error);
        return;
      }
      setState({ 
        isRunning: false, 
        stage: 'error', 
        progress: 0, 
        result: null, 
        error 
      });
      
      // Log error for debugging
      Sentry.logger.error('Speed test failed', { 
        error: error, 
        component: 'useSpeedTest',
      });
    };

    engine.play();
  }, []);

  const resetTest = useCallback(() => {
    engineRef.current?.pause();
    engineRef.current = null;
    setState({ 
      isRunning: false, 
      stage: 'idle', 
      progress: 0, 
      result: null, 
      error: null, 
    });
  }, []);

  return {
    state,
    runSpeedTest,
    resetTest,
    isRunning: state.isRunning,
    result: state.result,
    error: state.error,
    progress: state.progress,
    stage: state.stage,
  };
};